import {
  type ReactNode,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  useCallback,
} from 'react';
import { getCircularIndexByOffset } from '@/modules/utils/array';
import { StyledProductViewer360 } from './ProductViewer360.styles';
import { type BreakpointsProps } from '@/types';

export interface ProductViewer360Props
  extends HTMLAttributes<HTMLDivElement>,
    BreakpointsProps<Pick<ProductViewer360Props, 'width' | 'height'>> {
  width?: string;
  height?: string;
  children: ReactNode[];
  incrementPx?: number;
}

const ProductViewer360 = ({
  children,
  incrementPx = 15,
  width = '100%',
  height,
  ...restProps
}: ProductViewer360Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isScrolling, setIsScrolling] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  const addMultipleEventListeners = useCallback(
    (
      element: HTMLElement | Document | null,
      eventNames: string[],
      fn: (e: Event) => void
    ) => {
      if (element) {
        eventNames.forEach((eventName: string) => {
          element.addEventListener(eventName, fn);
        });
      }
    },
    []
  );

  const removeMultipleEventListeners = useCallback(
    (
      element: HTMLElement | Document | null,
      eventNames: string[],
      fn: (e: Event) => void
    ) => {
      if (element) {
        eventNames.forEach((eventName: string) => {
          element.removeEventListener(eventName, fn);
        });
      }
    },
    []
  );

  useEffect(() => {
    let lastPosition = 0;
    let nextIndex = 0;

    const handleTouchMove = (e: TouchEvent) => {
      handleMove(e.touches[0].clientX);
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX);
    };

    const handleMove = (clientX: number) => {
      if (isScrolling) {
        if (!lastPosition) lastPosition = clientX;

        const diff = lastPosition - clientX;

        if (diff > incrementPx || diff < -incrementPx) {
          if (lastPosition > clientX) {
            nextIndex++;
          } else {
            nextIndex--;
          }

          lastPosition = clientX;

          setImgIndex(getCircularIndexByOffset(children, 0, nextIndex));
        }
      }
    };

    const handleCancelScrollable = () => {
      setIsScrolling(false);
    };

    const handleEnableScrollable = (e: Event) => {
      setIsScrolling(true);
    };

    addMultipleEventListeners(
      ref.current,
      ['touchstart', 'mousedown'],
      handleEnableScrollable
    );

    addMultipleEventListeners(
      document,
      ['touchend', 'mouseup'],
      handleCancelScrollable
    );

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      removeMultipleEventListeners(
        ref.current,
        ['touchstart', 'mousedown'],
        handleEnableScrollable
      );

      removeMultipleEventListeners(
        document,
        ['touchend', 'mouseup'],
        handleCancelScrollable
      );

      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isScrolling]);

  return (
    <StyledProductViewer360
      ref={ref}
      isScrolling={isScrolling}
      imgIndex={imgIndex}
      width={width}
      height={height}
      {...restProps}
    >
      {children}
    </StyledProductViewer360>
  );
};

export default ProductViewer360;
